/**
 * Scoping the end-of-turn checks to what the agent changed.
 *
 * Asked for one change to an existing video, the agent used to fix every finding its whole-video
 * lint and review raised — recolouring text on scenes the user never mentioned, swapping their
 * music — which users read as the agent making changes they didn't ask for. So on an edit, lint and
 * review ask it to fix only what its change could have caused, and only to mention the rest:
 *
 * 1. At the user's message, `Conversation` captures the video state (`TurnEdits.start`): the same
 *    description of the video the agent reads.
 * 2. When lint or review runs, `changesSoFar` captures it again, and `whatChanged` reads off the
 *    difference: for each scene, which kinds of change it had (`changeKinds`), plus the music,
 *    the scene list, and whether the whole video was restyled.
 * 3. Each lint check declares which kinds of change can cause its findings (`causedBy` in
 *    `videoLinter`). A finding is the edit's when a scene it names was added, or changed in one of
 *    those ways. Review, which judges how scenes look, covers the scenes whose text or look changed.
 * 4. The starting state carries over to the next message until a full lint passes, so a build or
 *    edit that stopped part way is still the agent's to check when the user says "continue".
 *
 * A change to the whole video reaches every scene as the kind of change it is: a new aspect ratio is
 * a layout change to every scene, a template recolour a colour change. Builds from an empty video
 * and `lint_video` with scope "video" leave everything in scope.
 */
import type Luminary from '@lumen5/luminary';

import { type LintFinding, lintCheckCausedBy } from '../lint/videoLinter';
import { captureVideoState, type VideoStateSnapshot } from '../video-state';

import type { SceneChangeKind } from './changeKinds';
import { ANY_VISUAL_CHANGE, type VideoChanges, whatChanged } from './whatChanged';

import type { GetRootState } from 'js/src/libs/RootState';

/**
 * The section of a review result listing findings outside the turn's edits. They are still shown —
 * the user may want to hear about them — but as something to mention, not to fix.
 */
export function formatOutsideEdit(findingLines: readonly string[], maxShown: number): string {
    const hiddenCount = findingLines.length - maxShown;
    return [
        `Outside this edit — ${findingLines.length} finding(s) on parts of the video you didn't change this turn, so they were already there. Don't fix them: the user asked for a specific change, and fixing these changes things they didn't ask about. Mention them to the user in one short line and offer to fix them, unless the user asked you to check or fix the whole video.`,
        ...findingLines.slice(0, maxShown),
        ...(hiddenCount > 0 ? [`  ... and ${hiddenCount} more.`] : []),
    ].join('\n');
}

/** What a turn has changed so far, for lint and review to tell its defects from older ones. */
export interface TurnChanges {
    /** Whether some findings may lie outside this turn's edits; false once it touched everything. */
    readonly isScoped: boolean;
    /** Whether the turn changed how a scene looks in any way, or added it: what review judges. */
    coversScene(sceneId: string): boolean;
    /** Whether a lint finding could have been caused by this turn's edits. */
    coversFinding(finding: LintFinding): boolean;
}

/** A scene review looks at again: one that is new, or looks different in any way. */
const CHANGES_REVIEW_JUDGES: readonly (SceneChangeKind | 'added')[] = [
    'added',
    ...ANY_VISUAL_CHANGE,
];

/** Every finding is the agent's to fix: used when the video state couldn't be read. */
const UNSCOPED: TurnChanges = {
    isScoped: false,
    coversScene: () => true,
    coversFinding: () => true,
};

/** Reads the video state, or nothing when it can't be: a check is then simply left unscoped. */
async function tryCaptureVideoState(
    luminary: Luminary,
    getState: GetRootState | undefined,
): Promise<VideoStateSnapshot | undefined> {
    try {
        return await captureVideoState(luminary, undefined, getState);
    } catch {
        return undefined;
    }
}

/** The video state when the agent's current work began, and whether that work has passed lint. */
export class TurnEdits {
    private lintPassed = false;

    private constructor(
        private readonly luminary: Luminary,
        private readonly getState: GetRootState | undefined,
        private readonly atStart: VideoStateSnapshot | undefined,
    ) {}

    public static async start(luminary: Luminary, getState?: GetRootState): Promise<TurnEdits> {
        return new TurnEdits(luminary, getState, await tryCaptureVideoState(luminary, getState));
    }

    /** Whether a full lint has found nothing left to fix in what the agent changed. */
    public get hasPassedLint(): boolean {
        return this.lintPassed;
    }

    public markLintPassed(): void {
        this.lintPassed = true;
    }

    /** What the turn has changed so far. Take it before an auto-fix runs, so the fix isn't counted. */
    public async changesSoFar(): Promise<TurnChanges> {
        const now = await tryCaptureVideoState(this.luminary, this.getState);
        return this.atStart && now
            ? new ChangesSinceStart(whatChanged(this.atStart, now), now)
            : UNSCOPED;
    }
}

class ChangesSinceStart implements TurnChanges {
    constructor(
        private readonly changes: VideoChanges,
        private readonly now: VideoStateSnapshot,
    ) {}

    public get isScoped(): boolean {
        return !this.changes.isBuild;
    }

    public coversScene(sceneId: string): boolean {
        const kinds = this.changes.scenes.get(sceneId);
        return (
            this.changes.isBuild ||
            (!!kinds && CHANGES_REVIEW_JUDGES.some((kind) => kinds.has(kind)))
        );
    }

    public coversFinding({ check, sceneId, sceneGroupIndex }: LintFinding): boolean {
        if (this.changes.isBuild) {
            return true;
        }
        const causedBy = lintCheckCausedBy(check);
        const changedInAWayThatCounts = (id: string | undefined) => {
            const kinds = id === undefined ? undefined : this.changes.scenes.get(id);
            return !!kinds && (kinds.has('added') || causedBy.some((kind) => kinds.has(kind)));
        };
        if (sceneId !== undefined) {
            return (Array.isArray(sceneId) ? sceneId : [sceneId]).some(changedInAWayThatCounts);
        }
        if (sceneGroupIndex !== undefined) {
            return this.now.scenes
                .filter((scene) => scene.sceneGroupIndex === sceneGroupIndex)
                .some((scene) => changedInAWayThatCounts(scene.id));
        }
        return (
            (causedBy.includes('music') && this.changes.music) ||
            (causedBy.includes('scene_list') && this.changes.sceneList)
        );
    }
}
